import { analytics } from '@eti/utils';
import { useCallback, useRef } from 'react';

type LanguageChangeSource = 'Manual' | 'MarketDefault';

type SelectField = 'market' | 'language' | 'currency';

type TrackSaveParams = {
  selectedMarket: string;
  selectedLanguage: string;
  isMarketChanged: boolean;
  isLanguageChanged: boolean;
};

// Locale values are e.g. 'en-US'/'en-CA', but the spec calls for the bare language subtag.
const getLanguageCode = (locale: string) => locale.split('-')[0];

export const useSettingsDataLayerTracking = () => {
  const languageChangeSourceRef = useRef<LanguageChangeSource | undefined>(undefined);

  const trackOpen = useCallback((section: string) => {
    languageChangeSourceRef.current = undefined;
    analytics.pushEntryToDataLayer({
      event: 'regional_settings_open',
      eventData: { section },
    });
  }, []);

  const trackCancel = useCallback(() => {
    analytics.pushEntryToDataLayer({ event: 'regional_settings_cancel' });
  }, []);

  const trackSelect = useCallback((field: SelectField, value: string) => {
    if (field === 'market') {
      languageChangeSourceRef.current = 'MarketDefault';
    } else if (field === 'language') {
      languageChangeSourceRef.current = 'Manual';
    }
    analytics.pushEntryToDataLayer({
      event: `regional_settings_${field}_select`,
      eventData: { [`selected_${field}`]: field === 'language' ? getLanguageCode(value) : value },
    });
  }, []);

  const trackSave = useCallback(
    ({ selectedMarket, selectedLanguage, isMarketChanged, isLanguageChanged }: TrackSaveParams) => {
      analytics.pushEntryToDataLayer({
        event: 'regional_settings_save',
        eventData: {
          selected_market: isMarketChanged ? selectedMarket : undefined,
          selected_language: isLanguageChanged ? getLanguageCode(selectedLanguage) : undefined,
          market_changed: isMarketChanged,
          language_changed: isLanguageChanged,
          language_change_source: isLanguageChanged ? languageChangeSourceRef.current : undefined,
        },
      });
    },
    [],
  );

  return {
    trackOpen,
    trackCancel,
    trackSelect,
    trackSave,
  };
};
